import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { login } from '../store/slices/authSlice';
import { showSuccess, showError } from '../store/slices/toastSlice';
import { authApi } from '../services/authApi';
import { config } from '../config';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ username?: string; password?: string }>({});
  const [demoCredentials, setDemoCredentials] = useState<any[]>([]);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoModeToggle, setDemoModeToggle] = useState(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, error, isAuthenticated } = useAppSelector((state) => state.auth);

  useEffect(() => {
    // Redirect if already authenticated
    console.log('Login component - isAuthenticated changed:', isAuthenticated);
    if (isAuthenticated) {
      console.log('Login component - redirecting to dashboard');
      navigate('/admin/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Initialize demo mode toggle from config
    setDemoModeToggle(authApi.getDemoMode());
  }, []);

  useEffect(() => {
    // Check if we're in demo mode and get demo credentials
    const shouldUseDemo = demoModeToggle;
    setIsDemoMode(shouldUseDemo);
    
    // Update auth API demo mode
    authApi.setDemoMode(shouldUseDemo);
    
    if (shouldUseDemo) {
      try {
        const credentials = authApi.getDemoCredentials();
        setDemoCredentials(credentials);
      } catch (error) {
        console.warn('Failed to get demo credentials:', error);
        setDemoCredentials([]);
      }
    } else {
      setDemoCredentials([]);
    }
  }, [demoModeToggle]);

  const handleDemoModeToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDemoModeToggle(event.target.checked);
    // Clear form when switching modes
    setUsername('');
    setPassword('');
    setErrors({});
  };

  const validateForm = () => {
    const newErrors: { username?: string; password?: string } = {};

    if (!username.trim()) {
      newErrors.username = 'Username is required';
    }

    if (!password.trim()) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      console.log('Login component - dispatching login action');
      const result = await dispatch(login({ username, password })).unwrap();
      console.log('Login component - login successful:', result);
      dispatch(showSuccess({
        title: 'Login Successful',
        message: `Welcome back, ${username}!`,
        duration: 3000,
      }));
      // The navigation will be handled by the useEffect that watches isAuthenticated
    } catch (error: any) {
      dispatch(showError({
        title: 'Login Failed',
        message: error.message || 'Invalid username or password. Please try again.',
        duration: 5000,
      }));
    }
  };

  const handleDemoLogin = (demoUser: any) => {
    setUsername(demoUser.username);
    setPassword(demoUser.password);
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {/* Demo Mode Toggle */}
          <Box sx={{ width: '100%', mb: 2, display: 'flex', justifyContent: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={demoModeToggle}
                  onChange={handleDemoModeToggle}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  {demoModeToggle ? 'Demo Mode' : 'Real API Mode'}
                </Typography>
              }
            />
          </Box>

          {isDemoMode && (
            <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
              <strong>Demo Mode:</strong> Using demo credentials for testing.
            </Alert>
          )}

          {!isDemoMode && (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              <strong>Real API Mode:</strong> Connecting to backend at {config.apiUrl}
            </Alert>
          )}

          <Typography component="h1" variant="h4" gutterBottom>
            Brilliant Assistant Admin
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Sign in to access the admin panel
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              error={!!errors.username}
              helperText={errors.username}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!errors.password}
              helperText={errors.password}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </Button>
          </Box>

          {isDemoMode && demoCredentials.length > 0 && (
            <>
              <Divider sx={{ width: '100%', my: 2 }} />
              
              <Typography variant="h6" gutterBottom>
                Demo Credentials
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                Click on any credential below to auto-fill the form
              </Typography>

              <Grid container spacing={2} sx={{ width: '100%' }}>
                {demoCredentials.map((cred, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { 
                          borderColor: 'primary.main',
                          backgroundColor: 'action.hover'
                        }
                      }}
                      onClick={() => handleDemoLogin(cred)}
                    >
                      <CardContent sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="subtitle2" gutterBottom>
                          {cred.role.charAt(0).toUpperCase() + cred.role.slice(1)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Username: <strong>{cred.username}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Password: <strong>{cred.password}</strong>
                        </Typography>
                        <Chip 
                          label={cred.role} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ mt: 1 }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
